import config from '@config/index';
import type { PlaceLocation } from '@goodfynd/context';

import * as arrayUtil from '../array-util';
import * as dateUtil from '../date-util';

export const list = async ({ options = {} } = {}): Promise<
  gapi.client.calendar.CalendarListEntry[]
> => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await gapi.client.calendar.calendarList.list({
        ...options,
      });

      resolve(response.result.items || []);
    } catch (error) {
      console.error(error);
      reject(error);
    }
  });
};

export const listEvents = async ({
  id = 'primary',
  options = {},
}): Promise<gapi.client.calendar.Event[]> => {
  try {
    const minDate = new Date();
    const maxDate = dateUtil.endOfMonth(minDate);
    const response = await gapi.client.calendar.events.list({
      calendarId: id,
      orderBy: 'startTime',
      showDeleted: false,
      singleEvents: true,
      timeMax: maxDate.toISOString(),
      timeMin: minDate.toISOString(),
      ...options,
    });
    return response.result.items || [];
  } catch (error) {
    console.debug(error);
    return [];
  }
};

export type MonthEvent = {
  date: string;
  list: MonthEventItem[];
  number: number;
};

export type MonthEventItem = {
  end: Date;
  endLabel: string;
  key: string;
  missing?: boolean;
  name?: string;
  selected: boolean;
  start: Date;
  startLabel: string;
  timeLabel: string;
} & Partial<PlaceLocation>;

export const parseEvents = (
  events: gapi.client.calendar.Event[],
  savedLocations: { [key: string]: PlaceLocation },
  addDays = 0
) => {
  const monthEvents: MonthEvent[] = arrayUtil.create(32).map((_day, index) => {
    const monthNumber = index;
    const date = dateUtil.addDays(
      dateUtil.setDate(new Date(), monthNumber),
      addDays
    );
    return {
      date: dateUtil.format(date, config.dateFormats.weekDayMonthDay),
      list: [],
      number: monthNumber,
    };
  });

  for (let i = events.length - 1; i >= 0; i--) {
    const entry = events[i];
    if (
      entry.end?.dateTime &&
      entry.start?.dateTime &&
      dateUtil.isFuture(entry.start.dateTime)
    ) {
      const endTime = new Date(entry.end.dateTime);
      const startTime = new Date(entry.start.dateTime);
      const endLabel = dateUtil.format(endTime, config.dateFormats.time); // 'h:mm a'
      const startLabel = dateUtil.format(startTime, config.dateFormats.time);
      const date = dateUtil.getDate(startTime);
      const { list } = monthEvents[date];
      const total = list.length;
      const key = `entry-${total}-${i}`;
      const name = entry.summary?.trim() || 'Unknown Location';
      const locationKey = name.toLowerCase();
      const location = savedLocations[locationKey];

      const info: MonthEventItem = {
        end: endTime,
        endLabel,
        key,
        name,
        selected: false,
        start: startTime,
        startLabel,
        timeLabel: startLabel + config.unicode.dashEnPadded + endLabel,
        ...(location || {
          missing: true,
        }),
        address: location
          ? location.address === name
            ? location.coordinates
            : location.address
          : undefined,
      };
      list.push(info);
      monthEvents[date].list = list;
    }
  }

  console.debug(monthEvents);
  return monthEvents.filter((date) => date.list.length);
};
